import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  CAPACITY_UNITS,
  formatDate,
  formatHours,
  formatMonthShort,
  formatMonthYear,
  type CapacityCalendar as Calendar,
  type CapacityCell,
  type CapacityTask,
  type CapacityUnit,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { TONE_CELL, TONE_SWATCH } from '@/lib/report-tones';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Modal,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';

/**
 * The calendar on /rapporter/kapacitet: free hours per person, week by week - or month by month,
 * zoomed out - as far ahead as one scrolls. It takes the place of the report's 12-week table on the
 * page; the CSV and the PDF keep the table.
 *
 * Everything is computed by the API (GET /reports/capacity/calendar, see demo/reports/capacity.ts)
 * - the colours too. This component lays it out and formats it, nothing else.
 */

const UNIT_LABELS: Record<CapacityUnit, string> = { week: 'Uger', month: 'Måneder' };

type Period = Calendar['periods'][number];

function periodLabel(unit: CapacityUnit, period: Period): string {
  return unit === 'week' ? `Uge ${period.week ?? ''}` : formatMonthShort(period.start);
}

function periodSpan(period: Period): string {
  return `${formatDate(period.start)} – ${formatDate(period.end)}`;
}

/** The header above the periods: the month for weeks, the year for months. */
function groupsOf(unit: CapacityUnit, periods: readonly Period[]) {
  const groups: Array<{ key: string; label: string; span: number }> = [];
  for (const period of periods) {
    const key = unit === 'week' ? period.start.slice(0, 7) : period.start.slice(0, 4);
    const last = groups.at(-1);
    if (last?.key === key) last.span += 1;
    else {
      groups.push({
        key,
        label: unit === 'week' ? formatMonthYear(period.start) : key,
        span: 1,
      });
    }
  }
  return groups;
}

function readyLabel(unit: CapacityUnit, periods: readonly Period[], index: number | null): string {
  if (index === null) return 'Ikke i perioden';
  if (index === 0) return unit === 'week' ? 'Denne uge' : 'Denne måned';
  const period = periods[index];
  if (!period) return '—';
  return unit === 'week' ? `Uge ${period.week ?? ''}` : formatMonthYear(period.start);
}

function cellTitle(label: string, cell: CapacityCell): string {
  const carried = cell.carried_in > 0 ? ` (+ ${formatHours(cell.carried_in)} skubbet fra før)` : '';
  return (
    `${label}: til rådighed ${formatHours(cell.available)} · booket ${formatHours(cell.booked)}` +
    `${carried} · ledigt ${formatHours(cell.free)}`
  );
}

/* Narrower on a phone: the name column stays put, and 192 px of 343 left little to scroll. */
const NAME_CELL =
  'sticky left-0 z-10 w-32 min-w-32 border-b border-r border-line px-3 py-2 text-left ' +
  'sm:w-48 sm:min-w-48';

/** A clickable number in a cell - the whole cell is the target. */
const CELL_BUTTON = 'w-full text-right tabular underline-offset-2 hover:underline';

/** What a click on a cell opened: its period, what it adds up to, and the tasks behind it. */
interface OpenCell {
  title: string;
  summary: string;
  tasks: CapacityTask[];
}

export function CapacityCalendar() {
  const [unit, setUnit] = useState<CapacityUnit>('week');
  const [opened, setOpened] = useState<OpenCell | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, isPlaceholderData } = useQuery({
    queryKey: ['report', 'capacity', 'calendar', unit],
    queryFn: () => api.get<Calendar>('/reports/capacity/calendar', { unit }),
    // The other zoom stays on screen while this one loads, so the page does not jump.
    placeholderData: keepPreviousData,
  });

  /* A new zoom starts at today. */
  useEffect(() => {
    scroller.current?.scrollTo({ left: 0 });
  }, [unit]);

  const scrollBy = (direction: -1 | 1): void => {
    const element = scroller.current;
    if (!element) return;
    element.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: 'smooth' });
  };

  const width = unit === 'week' ? 'min-w-[76px]' : 'min-w-[96px]';

  return (
    <Card className="mb-5">
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle>Kalender</CardTitle>
          <p className="mt-1 text-xs text-ink-muted">
            Ledige timer pr. medarbejder {unit === 'week' ? 'og uge' : 'og måned'} — normtiden gange
            målet for faktureringsgrad, minus det bookede. Rul til siden for at se længere frem;
            tryk på en celle for regnestykket og opgaverne bag det.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="Visning"
            className="inline-flex rounded-md border border-line bg-surface p-0.5"
          >
            {CAPACITY_UNITS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={unit === option}
                onClick={() => setUnit(option)}
                className={cn(
                  'h-7 rounded px-3 text-xs font-medium',
                  unit === option ? 'bg-brand text-white' : 'text-ink hover:bg-canvas',
                )}
              >
                {UNIT_LABELS[option]}
              </button>
            ))}
          </div>
          <Button
            size="icon"
            variant="secondary"
            aria-label="Tidligere"
            onClick={() => scrollBy(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => scroller.current?.scrollTo({ left: 0, behavior: 'smooth' })}
          >
            I dag
          </Button>
          <Button size="icon" variant="secondary" aria-label="Senere" onClick={() => scrollBy(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {isLoading ? (
        <Skeleton className="m-4 h-48" />
      ) : error || !data ? (
        <Alert tone="error" className="m-4">
          Kunne ikke hente kalenderen.
        </Alert>
      ) : (
        <>
          {data.warnings.length > 0 ? (
            <Alert tone="warning" className="m-4">
              {data.warnings.join(' ')}
            </Alert>
          ) : null}

          <div
            ref={scroller}
            className={cn('overflow-x-auto', isPlaceholderData && 'opacity-60 transition-opacity')}
          >
            <table className="border-separate border-spacing-0 text-xs">
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    scope="col"
                    className={cn(NAME_CELL, 'bg-canvas align-bottom font-semibold text-ink-muted')}
                  >
                    Medarbejder
                  </th>
                  {groupsOf(data.unit, data.periods).map((group) => (
                    <th
                      key={group.key}
                      colSpan={group.span}
                      scope="colgroup"
                      className="border-b border-r border-line bg-canvas px-2 py-1.5 text-left font-semibold text-ink"
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {data.periods.map((period, index) => (
                    <th
                      key={period.start}
                      scope="col"
                      title={periodSpan(period)}
                      className={cn(
                        width,
                        'border-b border-r border-line bg-canvas px-2 py-1.5 text-right font-medium',
                        index === 0 ? 'text-brand' : 'text-ink-muted',
                      )}
                    >
                      {periodLabel(data.unit, period)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.people.length === 0 ? (
                  <tr>
                    <td colSpan={data.periods.length + 1} className="px-3 py-6 text-ink-muted">
                      Ingen medarbejdere at vise.
                    </td>
                  </tr>
                ) : null}

                {data.people.map((person) => (
                  <tr key={person.id}>
                    <th scope="row" className={cn(NAME_CELL, 'bg-surface font-medium')}>
                      <span className="block truncate" title={person.name}>
                        {person.name}
                      </span>
                      <span className="block font-normal text-ink-muted">
                        Ledig fra {readyLabel(data.unit, data.periods, person.ready).toLowerCase()}
                      </span>
                    </th>
                    {person.cells.map((cell, index) => {
                      const period = data.periods[index]!;
                      const label = periodLabel(data.unit, period);
                      return (
                        <td
                          key={period.start}
                          title={cellTitle(label, cell)}
                          className={cn(
                            'border-b border-r border-line px-2 py-2 text-right tabular',
                            TONE_CELL[cell.tone],
                          )}
                        >
                          <button
                            type="button"
                            className={CELL_BUTTON}
                            onClick={() =>
                              setOpened({
                                title: `${person.name} · ${label}`,
                                summary: cellTitle(label, cell),
                                tasks: cell.tasks,
                              })
                            }
                          >
                            {formatHours(cell.free)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {data.unassigned.some((entry) => entry.planned > 0 || entry.taken > 0) ? (
                  <tr>
                    <th scope="row" className={cn(NAME_CELL, 'bg-surface font-medium')}>
                      Ikke tildelt
                      <span className="block font-normal text-ink-muted">trækkes fra holdet</span>
                    </th>
                    {data.unassigned.map((entry, index) => {
                      const period = data.periods[index]!;
                      const label = periodLabel(data.unit, period);
                      const summary =
                        `${label}: planlagt ${formatHours(entry.planned)} · taget af holdet ` +
                        formatHours(entry.taken);
                      const value = entry.taken > 0 ? formatHours(-entry.taken) : '—';
                      return (
                        <td
                          key={period.start}
                          title={summary}
                          className="border-b border-r border-line px-2 py-2 text-right tabular text-ink-muted"
                        >
                          {entry.tasks.length > 0 ? (
                            <button
                              type="button"
                              className={CELL_BUTTON}
                              onClick={() =>
                                setOpened({
                                  title: `Ikke tildelt · ${label}`,
                                  summary,
                                  tasks: entry.tasks,
                                })
                              }
                            >
                              {value}
                            </button>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ) : null}

                <tr>
                  <th scope="row" className={cn(NAME_CELL, 'bg-canvas font-semibold')}>
                    Holdet
                    <span className="block font-normal text-ink-muted">
                      Plads fra {readyLabel(data.unit, data.periods, data.team_ready).toLowerCase()}
                    </span>
                  </th>
                  {data.team.map((cell, index) => {
                    const period = data.periods[index]!;
                    return (
                      <td
                        key={period.start}
                        title={
                          `${periodLabel(data.unit, period)}: til rådighed ` +
                          `${formatHours(cell.available)} · ledigt efter de ikke tildelte ` +
                          formatHours(cell.free)
                        }
                        className={cn(
                          'border-b border-r border-line px-2 py-2 text-right font-semibold tabular',
                          TONE_CELL[cell.tone],
                        )}
                      >
                        {formatHours(cell.free)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          <ul className="flex flex-wrap gap-x-5 gap-y-1 px-4 py-3 text-xs text-ink-muted">
            {(
              [
                ['success', 'Mindst halvdelen ledig'],
                ['warning', 'Delvist booket'],
                ['danger', 'Fuldt booket'],
                ['neutral', 'Væk hele perioden'],
              ] as const
            ).map(([tone, label]) => (
              <li key={tone} className="flex items-center gap-1.5">
                <span aria-hidden className={cn('h-3 w-3 rounded-sm', TONE_SWATCH[tone])} />
                {label}
              </li>
            ))}
            <li>
              Holdet er grønt, når der er plads til et nyt projekt: mindst{' '}
              {formatHours(data.project_hours)} timer ledige efter de ikke tildelte opgaver.
            </li>
            <li>Tryk på en celle for at se de opgaver, der booker den.</li>
          </ul>
        </>
      )}

      <Modal
        open={opened !== null}
        onClose={() => setOpened(null)}
        title={opened?.title ?? ''}
        description={opened?.summary}
        size="lg"
      >
        {opened ? <CellTasks tasks={opened.tasks} /> : null}
      </Modal>
    </Card>
  );
}

/** The tasks behind a cell - their hours in the period, as planned. */
function CellTasks({ tasks }: { tasks: readonly CapacityTask[] }) {
  if (tasks.length === 0) {
    return <p className="text-sm text-ink-muted">Ingen opgaver booker perioden.</p>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <Th>Opgave</Th>
          <Th>Tavle</Th>
          <Th className="text-right">Timer</Th>
          <Th className="text-right">Deadline</Th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((task, index) => (
          <Tr key={`${task.name}-${index}`}>
            <Td className="text-xs">{task.name}</Td>
            <Td className="text-xs text-ink-muted">{task.board ?? '—'}</Td>
            <Td className="whitespace-nowrap text-right text-xs tabular">
              {formatHours(task.hours)}
            </Td>
            <Td className="whitespace-nowrap text-right text-xs tabular">
              {formatDate(task.deadline)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
